import React, { memo } from 'react';
import { Clock } from 'lucide-react';

import ProgressBreakdown from 'components/logistics/fulfillmentLifecycle/components/ProgressBreakdown';
import RiskBadge from 'components/logistics/fulfillmentLifecycle/components/RiskBadge';
import {
    formatAgingTime,
    formatPullTime,
    getPullTimeDelta,
    getRouteName,
    getStatusMessage,
} from 'components/logistics/fulfillmentLifecycle/utils/formatters';
import { Badge } from 'shadcn/components/ui/Badge';
import { Card, CardContent, CardHeader } from 'shadcn/components/ui/Card';
import { cn } from 'shadcn/lib/utils';

/**
 * Border color by risk level
 */
const RISK_BORDER_CLASSES = {
    CRITICAL: 'border-red-500 border-2',
    AT_RISK: 'border-amber-500 border-2',
    ON_TRACK: '',
};

/**
 * RouteCard - Displays route status information with risk indicators
 *
 * @param {Object} props
 * @param {Object} props.route - Route data object
 * @param {Function} [props.onClick] - Click handler
 * @param {Date} props.referenceTime - Reference time for "Pull Xm ago" calculations
 * @param {string} [props.className] - Additional CSS classes
 */
function RouteCard({ className, onClick, referenceTime, route }) {
    const {
        breakdowns,
        carrier,
        hub,
        labelsAgingMinutes,
        labelsCount,
        packedCount,
        packingAgingMinutes,
        pullTime,
        riskLevel,
        totalShipments,
        transitCount,
    } = route;

    const routeName = getRouteName(route);
    const borderClass = RISK_BORDER_CLASSES[riskLevel] || '';
    const hasLabelsAging = labelsAgingMinutes !== undefined && labelsAgingMinutes !== null;
    const hasPackingAging = packingAgingMinutes !== undefined && packingAgingMinutes !== null;

    // Compute status message (e.g. "Pull 60m ago, 28 not loaded") using reference time
    const pullTimeAgo = getPullTimeDelta(pullTime, referenceTime);
    const pullTimeAgoMinutes = pullTimeAgo != null && pullTimeAgo > 0 ? pullTimeAgo : null;
    const statusMessage = getStatusMessage(route, pullTimeAgoMinutes);

    const handleClick = () => {
        if (onClick) {
            onClick(route);
        }
    };

    return (
        <Card
            className={cn(borderClass, onClick && 'cursor-pointer hover:shadow-md transition-shadow', className)}
            onClick={handleClick}
        >
            <CardHeader className="pb-2">
                {/* Risk Badge */}
                <div className="flex justify-between items-start">
                    <RiskBadge riskLevel={riskLevel} />
                </div>

                {/* Hub + Pull Time Header */}
                <div className="flex justify-between items-baseline mt-2">
                    <h3 className="text-2xl font-bold">{hub}</h3>
                    <span className="text-xl font-semibold text-muted-foreground">
                        {formatPullTime(pullTime) ?? pullTime ?? '—'}
                    </span>
                </div>

                {/* Carrier */}
                <p className="text-sm text-muted-foreground">{carrier}</p>

                {/* Route Identifier */}
                <p className="text-sm text-muted-foreground">{routeName}</p>

                {/* Status message (e.g. "Pull 60m ago, 28 not loaded") */}
                {statusMessage && <p className="text-sm font-medium text-muted-foreground mt-1">{statusMessage}</p>}
            </CardHeader>

            <CardContent className="space-y-4">
                {/* Labels Status with Aging */}
                {hasLabelsAging && (
                    <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">Labels - {formatAgingTime(labelsAgingMinutes)}</span>
                        <Badge variant="outline" className="text-xs">
                            Aging
                        </Badge>
                    </div>
                )}

                {/* Packing Status with Aging */}
                {hasPackingAging && (
                    <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">Packing - {formatAgingTime(packingAgingMinutes)}</span>
                        <Badge variant="outline" className="text-xs">
                            Aging
                        </Badge>
                    </div>
                )}

                {/* Separator */}
                <div className="border-t" />

                {/* Progress Breakdown */}
                <ProgressBreakdown
                    breakdowns={breakdowns}
                    labelsCount={labelsCount}
                    packedCount={packedCount}
                    transitCount={transitCount}
                    totalShipments={totalShipments}
                />
            </CardContent>
        </Card>
    );
}

export default memo(RouteCard);
